import {
  FeatureFlagsContext,
  HorizontalFlexContainer,
  SiteUrlsContext,
  VerticalFlexContainer,
  vizPortalFontClass,
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { PublishedDatasourceCustomSqlQueryWrapper } from '../CustomSql/PublishedDatasourceCustomSqlQueryWrapper';
import { VirtualConnectionCustomSqlQueryWrapper } from '../CustomSql/VirtualConnectionCustomSqlWrapper';
import { memoizedGetDatabaseLineageConfiguration } from '../DatabasePlacePage/DatabaseLineageConfiguration';
import { DatabaseLineageCounts } from '../DatabasePlacePage/DatabaseLineageCountsQueryWrapper';
import { memoizedGetDatasourceLineageConfiguration } from '../DatasourcePlacePage/DatasourceLineageConfiguration';
import { DatasourceLineageCounts } from '../DatasourcePlacePage/DatasourceLineageCountsQueryWrapper';
import { memoizedGetFlowLineageConfiguration } from '../FlowPlacePage/FlowLineageConfiguration';
import { FlowLineageCounts } from '../FlowPlacePage/FlowLineageCountsQueryWrapper';
import { memoizedGetLensLineageConfiguration } from '../LensPlacePage/LensLineageConfiguration';
import { LensLineageCounts } from '../LensPlacePage/LensLineageCountsQueryWrapper';
import { memoizedGetTableLineageConfiguration } from '../TablePlacePage/TableLineageConfiguration';
import { TableLineageCounts } from '../TablePlacePage/TableLineageCountsQueryWrapper';
import { VirtualConnectionLineageCounts } from '../VirtualConnectionPlacePage/VirtualConnectionCountsQueryWrapper';
import { memoizedGetVirtualConnectionLineageConfiguration } from '../VirtualConnectionPlacePage/VirtualConnectionLineageConfiguration';
import { VirtualConnectionTableLineageCounts } from '../VirtualConnectionTablePlacePage/VirtualConnectionTableCountsQueryWrapper';
import { memoizedGetVirtualConnectionTableLineageConfiguration } from '../VirtualConnectionTablePlacePage/VirtualConnectionTableLineageConfiguration';
import { memoizedGetWorkbookLineageConfiguration } from '../WorkbookPlacePage/WorkbookLineageConfiguration';
import { WorkbookLineageCounts } from '../WorkbookPlacePage/WorkbookLineageCountsQueryWrapper';
import { LineageItemType } from '../lineage/LineageItemType';
import { LineageNav } from '../lineage/LineageNav';
import { LineageSectionType } from '../placePage/LineageConfiguration';

export interface LineageSubplaceLayoutProps<TCounts> {
  readonly pane: JSX.Element;
  readonly activeLineageItemKey: string;
  readonly filteredOnItemType: LineageItemType | null;
  readonly selectedAnchorItemIds: ReadonlyArray<string>;
  readonly urlSearchValue: string;
  readonly anchorName: string | null;
  readonly anchorCount: number | null;
  readonly countsLoading: boolean;
  readonly unfilteredCounts: TCounts | null;
  readonly filteredCounts: TCounts | null;
  readonly filteredItemName: string | null;
  readonly lineageSectionType?: LineageSectionType;
}

export type DatasourceLineageSubplaceLayoutProps = LineageSubplaceLayoutProps<DatasourceLineageCounts> & {
  readonly vizportalUrlId: string;
  readonly clearFieldSelection: () => void;
};

export type TableLineageSubplaceLayoutProps = LineageSubplaceLayoutProps<TableLineageCounts> & {
  readonly tableId: string;
  readonly clearColumnSelection: () => void;
};

export type DatabaseLineageSubplaceLayoutProps = LineageSubplaceLayoutProps<DatabaseLineageCounts> & {
  readonly databaseId: string;
  readonly clearTableSelection: () => void;
};

export type WorkbookLineageSubplaceLayoutProps = LineageSubplaceLayoutProps<WorkbookLineageCounts> & {
  readonly workbookId: string;
  readonly clearFieldSelection: () => void;
};

export type FlowLineageSubplaceLayoutProps = LineageSubplaceLayoutProps<FlowLineageCounts> & {
  readonly flowVizportalUrlId: string;
  readonly shouldShowFlowParametersWarning?: boolean;
  readonly clearFieldSelection: () => void;
};

export type LensLineageSubplaceLayoutProps = LineageSubplaceLayoutProps<LensLineageCounts> & {
  readonly vizportalUrlId: string;
};

export type VirtualConnectionLineageSubplaceLayoutProps = LineageSubplaceLayoutProps<VirtualConnectionLineageCounts> & {
  readonly virtualConnectionId: string;
  readonly clearTableSelection: () => void;
};

export type VirtualConnectionTableLineageSubplaceLayoutProps = LineageSubplaceLayoutProps<VirtualConnectionTableLineageCounts> & {
  readonly tableId: string;
  readonly clearColumnSelection: () => void;
};

export function DatasourceLineageSubplaceLayout(props: DatasourceLineageSubplaceLayoutProps): JSX.Element {
  return (
    <FeatureFlagsContext.Consumer>
      {
        featureFlags =>
          <HorizontalFlexContainer fillFlexParent={true} additionalClassNames={[vizPortalFontClass]}>
            <VerticalFlexContainer fillFlexParent={true}>
              {featureFlags.incompleteCustomSqlEnabled && props.lineageSectionType === LineageSectionType.Anchor ?
                <VerticalFlexContainer>
                  <PublishedDatasourceCustomSqlQueryWrapper
                    publishedDatasourceId={props.vizportalUrlId}
                  />
                </VerticalFlexContainer>
                : null
              }
              {props.pane}
            </VerticalFlexContainer>
            <SiteUrlsContext.Consumer>
              {urls => (
                <LineageNav
                  itemId={props.vizportalUrlId}
                  countsLoading={props.countsLoading}
                  unfilteredCounts={props.unfilteredCounts}
                  filteredCounts={props.filteredCounts}
                  urlSearchValue={props.urlSearchValue}
                  clearFilters={props.clearFieldSelection}
                  filteredItemName={props.filteredItemName}
                  activeLineageItemKey={props.activeLineageItemKey}
                  lineageConfiguration={memoizedGetDatasourceLineageConfiguration(featureFlags.metricsDataCatalogEnabled, featureFlags.publishedConnectionsLineageEnabled)}
                  urls={urls}
                  anchorName={props.anchorName}
                  anchorCount={props.anchorCount}
                  filteredOnItemType={props.filteredOnItemType}
                />
              )}
            </SiteUrlsContext.Consumer>
          </HorizontalFlexContainer>
      }
    </FeatureFlagsContext.Consumer>
  );
}

export function TableLineageSubplaceLayout(props: TableLineageSubplaceLayoutProps): JSX.Element {
  return (
    // vizPortalFontClass is already applied to the ancestor component CatalogBrowser
    <HorizontalFlexContainer fillFlexParent={true}>
      <VerticalFlexContainer fillFlexParent={true}>
        {props.pane}
      </VerticalFlexContainer>
      <SiteUrlsContext.Consumer>
        {urls => (
          <FeatureFlagsContext.Consumer>
            {
              featureFlags =>
                <LineageNav
                  itemId={props.tableId}
                  countsLoading={props.countsLoading}
                  unfilteredCounts={props.unfilteredCounts}
                  filteredCounts={props.filteredCounts}
                  urlSearchValue={props.urlSearchValue}
                  filteredItemName={props.filteredItemName}
                  clearFilters={props.clearColumnSelection}
                  activeLineageItemKey={props.activeLineageItemKey}
                  lineageConfiguration={memoizedGetTableLineageConfiguration(featureFlags.metricsDataCatalogEnabled, featureFlags.publishedConnectionsLineageEnabled)}
                  urls={urls}
                  anchorName={props.anchorName}
                  anchorCount={props.anchorCount}
                  filteredOnItemType={props.filteredOnItemType}
                />
            }
          </FeatureFlagsContext.Consumer>
        )}
      </SiteUrlsContext.Consumer>
    </HorizontalFlexContainer>
  );
}

export function DatabaseLineageSubplaceLayout(props: DatabaseLineageSubplaceLayoutProps): JSX.Element {
  return (
    // vizPortalFontClass is already applied to the ancestor component CatalogBrowser
    <HorizontalFlexContainer fillFlexParent={true}>
      <VerticalFlexContainer fillFlexParent={true}>
        {props.pane}
      </VerticalFlexContainer>
      <SiteUrlsContext.Consumer>
        {urls => (
          <FeatureFlagsContext.Consumer>
            {
              featureFlags =>
                <LineageNav
                  itemId={props.databaseId}
                  countsLoading={props.countsLoading}
                  unfilteredCounts={props.unfilteredCounts}
                  filteredCounts={props.filteredCounts}
                  urlSearchValue={props.urlSearchValue}
                  filteredItemName={props.filteredItemName}
                  clearFilters={props.clearTableSelection}
                  activeLineageItemKey={props.activeLineageItemKey}
                  lineageConfiguration={memoizedGetDatabaseLineageConfiguration(featureFlags.metricsDataCatalogEnabled, featureFlags.publishedConnectionsLineageEnabled)}
                  urls={urls}
                  anchorName={props.anchorName}
                  anchorCount={props.anchorCount}
                  filteredOnItemType={props.filteredOnItemType}
                />
            }
          </FeatureFlagsContext.Consumer>
        )}
      </SiteUrlsContext.Consumer>
    </HorizontalFlexContainer>
  );
}

export function WorkbookLineageSubplaceLayout(props: WorkbookLineageSubplaceLayoutProps): JSX.Element {
  return (
    <HorizontalFlexContainer fillFlexParent={true} additionalClassNames={[vizPortalFontClass]}>
      <VerticalFlexContainer fillFlexParent={true}>
        {props.pane}
      </VerticalFlexContainer>
      <SiteUrlsContext.Consumer>
        {urls => (
          <FeatureFlagsContext.Consumer>
            {
              featureFlags =>
                <LineageNav
                  itemId={props.workbookId}
                  countsLoading={props.countsLoading}
                  unfilteredCounts={props.unfilteredCounts}
                  filteredCounts={props.filteredCounts}
                  urlSearchValue={props.urlSearchValue}
                  filteredItemName={props.filteredItemName}
                  clearFilters={props.clearFieldSelection}
                  activeLineageItemKey={props.activeLineageItemKey}
                  lineageConfiguration={memoizedGetWorkbookLineageConfiguration(featureFlags.metricsDataCatalogEnabled,
                    featureFlags.incompleteCustomSqlEnabled, featureFlags.publishedConnectionsLineageEnabled)}
                  urls={urls}
                  anchorName={props.anchorName}
                  anchorCount={props.anchorCount}
                  filteredOnItemType={props.filteredOnItemType}
                />
            }
          </FeatureFlagsContext.Consumer>
        )}
      </SiteUrlsContext.Consumer>
    </HorizontalFlexContainer>
  );
}

export function FlowLineageSubplaceLayout(props: FlowLineageSubplaceLayoutProps): JSX.Element {
  return (
    <FeatureFlagsContext.Consumer>
      {
        featureFlags =>
          <HorizontalFlexContainer fillFlexParent={true} additionalClassNames={[vizPortalFontClass]}>
            <VerticalFlexContainer fillFlexParent={true}>
              {props.pane}
            </VerticalFlexContainer>
            <SiteUrlsContext.Consumer>
              {urls => (
                <LineageNav
                  itemId={props.flowVizportalUrlId}
                  countsLoading={props.countsLoading}
                  unfilteredCounts={props.unfilteredCounts}
                  filteredCounts={props.filteredCounts}
                  urlSearchValue={props.urlSearchValue}
                  filteredItemName={props.filteredItemName}
                  clearFilters={props.clearFieldSelection}
                  activeLineageItemKey={props.activeLineageItemKey}
                  lineageConfiguration={memoizedGetFlowLineageConfiguration(featureFlags.metricsDataCatalogEnabled,
                    featureFlags.publishedConnectionsLineageEnabled, featureFlags.incompleteCustomSqlEnabled)}
                  urls={urls}
                  anchorName={props.anchorName}
                  anchorCount={props.anchorCount}
                  filteredOnItemType={props.filteredOnItemType}
                  shouldShowFlowParametersWarning={props.shouldShowFlowParametersWarning}
                />
              )}
            </SiteUrlsContext.Consumer>
          </HorizontalFlexContainer>
      }
    </FeatureFlagsContext.Consumer>
  );
}

export function LensLineageSubplaceLayout(props: LensLineageSubplaceLayoutProps): JSX.Element {
  return (
    <FeatureFlagsContext.Consumer>
      {
        _ =>
          <HorizontalFlexContainer fillFlexParent={true} additionalClassNames={[vizPortalFontClass]}>
            <VerticalFlexContainer fillFlexParent={true}>
              {props.pane}
            </VerticalFlexContainer>
            <SiteUrlsContext.Consumer>
              {urls => (
                <LineageNav
                  itemId={props.vizportalUrlId}
                  countsLoading={props.countsLoading}
                  unfilteredCounts={props.unfilteredCounts}
                  filteredCounts={props.filteredCounts}
                  urlSearchValue={props.urlSearchValue}
                  clearFilters={() => { return; }}
                  filteredItemName={props.filteredItemName}
                  activeLineageItemKey={props.activeLineageItemKey}
                  lineageConfiguration={memoizedGetLensLineageConfiguration()}
                  urls={urls}
                  anchorName={props.anchorName}
                  anchorCount={props.anchorCount}
                  filteredOnItemType={props.filteredOnItemType}
                />
              )}
            </SiteUrlsContext.Consumer>
          </HorizontalFlexContainer>
      }
    </FeatureFlagsContext.Consumer>
  );
}


export function VirtualConnectionLineageSubplaceLayout(props: VirtualConnectionLineageSubplaceLayoutProps): JSX.Element {
  return (
    <FeatureFlagsContext.Consumer>
      {
        featureFlags =>
          <HorizontalFlexContainer fillFlexParent={true} additionalClassNames={[vizPortalFontClass]}>
            <VerticalFlexContainer fillFlexParent={true}>
              {featureFlags.incompleteCustomSqlEnabled && props.lineageSectionType === LineageSectionType.Anchor ?
                <VerticalFlexContainer>
                  <VirtualConnectionCustomSqlQueryWrapper
                    virtualConnectionId={props.virtualConnectionId}
                  />
                </VerticalFlexContainer>
                : null
              }
              {props.pane}
            </VerticalFlexContainer>
            <SiteUrlsContext.Consumer>
              {urls => (
                <LineageNav
                  itemId={props.virtualConnectionId}
                  countsLoading={props.countsLoading}
                  unfilteredCounts={props.unfilteredCounts}
                  filteredCounts={props.filteredCounts}
                  urlSearchValue={props.urlSearchValue}
                  filteredItemName={props.filteredItemName}
                  clearFilters={props.clearTableSelection}
                  activeLineageItemKey={props.activeLineageItemKey}
                  lineageConfiguration={memoizedGetVirtualConnectionLineageConfiguration()}
                  urls={urls}
                  anchorName={props.anchorName}
                  anchorCount={props.anchorCount}
                  filteredOnItemType={props.filteredOnItemType}
                />
              )}
            </SiteUrlsContext.Consumer>
          </HorizontalFlexContainer>
      }
    </FeatureFlagsContext.Consumer>
  );
}

export function VirtualConnectionTableLineageSubplaceLayout(props: VirtualConnectionTableLineageSubplaceLayoutProps): JSX.Element {
  return (
    <HorizontalFlexContainer fillFlexParent={true} additionalClassNames={[vizPortalFontClass]}>
      <VerticalFlexContainer fillFlexParent={true}>
        {props.pane}
      </VerticalFlexContainer>
      <SiteUrlsContext.Consumer>
        {urls => (
          <LineageNav
            itemId={props.tableId}
            countsLoading={props.countsLoading}
            unfilteredCounts={props.unfilteredCounts}
            filteredCounts={props.filteredCounts}
            urlSearchValue={props.urlSearchValue}
            filteredItemName={props.filteredItemName}
            clearFilters={props.clearColumnSelection}
            activeLineageItemKey={props.activeLineageItemKey}
            lineageConfiguration={memoizedGetVirtualConnectionTableLineageConfiguration()}
            urls={urls}
            anchorName={props.anchorName}
            anchorCount={props.anchorCount}
            filteredOnItemType={props.filteredOnItemType}
          />
        )}
      </SiteUrlsContext.Consumer>
    </HorizontalFlexContainer>
  );
}
